import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { Suspense, useEffect } from "react";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { useFilterRequestStartTimeSlots } from "@/hooks/useFilterRequestStartTimeSlots/useFilterRequestStartTimeSlots";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { Loader } from "@whoosh/design-system/src/components/display";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { setRequestInfoAtom } from "../../../../atoms/actions/setRequestInfo/setRequestInfoAtom";
import { getRequestInfoAtom } from "../../../../atoms/selectors/getRequestInfo/getRequestInfoAtom";
import {
  useGetEarlyTimeOptions,
  useGetLateTimeOptions,
  useGetTimeOptions,
} from "../../../../hooks/useGetTimeOptions/useGetTimeOptions";
import { RequestTimeSelectionControlLessonAvailabilityQuery } from "./__generated__/RequestTimeSelectionControlLessonAvailabilityQuery.graphql";
import { RequestTimeSelectionControl_TimeSlots$key } from "./__generated__/RequestTimeSelectionControl_TimeSlots.graphql";

type Props = {
  timeSlotsFragmentRef: RequestTimeSelectionControl_TimeSlots$key | null;
  isLessonBooking: boolean;
  instructorId?: string | null;
  lessonTypeId?: string | null;
  durationMins?: number | null;
  facilityId?: string | null;
  date?: string | null;
};

type TimeSlotOption = {
  readonly id: string;
  readonly time: string;
  readonly availability?: string | null;
};

/**
 * Component for selecting the time options in a request booking
 */
export const RequestTimeSelectionControl = ({
  timeSlotsFragmentRef,
  isLessonBooking,
  instructorId,
  lessonTypeId,
  durationMins,
  facilityId,
  date,
}: Props) => {
  const timeSlotsData = useFragment(
    TimeSlotsFragmentString,
    timeSlotsFragmentRef
  );
  const [lessonAvailabilityQueryRef, loadLessonAvailabilityQuery] =
    useQueryLoader<RequestTimeSelectionControlLessonAvailabilityQuery>(
      LessonAvailabilityQueryString
    );

  const agendaTimeSlots =
    timeSlotsData?.timeSlots?.edges?.map((edge) => ({
      id: edge?.node?.id || "",
      time: edge?.node?.time || "",
      availability: edge?.node?.availability,
    })) ?? [];

  const hasLessonAvailabilityArgs = Boolean(
    isLessonBooking &&
      instructorId &&
      lessonTypeId &&
      durationMins &&
      facilityId &&
      date
  );

  useEffect(() => {
    if (!hasLessonAvailabilityArgs) {
      return;
    }

    loadLessonAvailabilityQuery(
      {
        instructorId: instructorId!,
        dateTimeGte: `${date!}T00:00:00`,
        dateTimeLte: `${date!}T23:59:59`,
        durationMins: durationMins!,
        slotAvailability: {
          bookingType: "LESSON",
          facilityId: facilityId!,
          instructorIds: [instructorId!],
          lessonTypeId: lessonTypeId!,
        },
      },
      { fetchPolicy: "network-only" }
    );
  }, [
    date,
    durationMins,
    facilityId,
    instructorId,
    lessonTypeId,
    loadLessonAvailabilityQuery,
    hasLessonAvailabilityArgs,
  ]);

  if (isLessonBooking) {
    return hasLessonAvailabilityArgs && lessonAvailabilityQueryRef ? (
      <Suspense fallback={<Loader />}>
        <LessonAvailabilityRequestTimeSelectionControl
          queryRef={lessonAvailabilityQueryRef}
        />
      </Suspense>
    ) : null;
  }

  return <RequestTimeSelectionForm timeSlots={agendaTimeSlots} />;
};

const LessonAvailabilityRequestTimeSelectionControl = ({
  queryRef,
}: {
  queryRef: PreloadedQuery<RequestTimeSelectionControlLessonAvailabilityQuery>;
}) => {
  const data = usePreloadedQuery(LessonAvailabilityQueryString, queryRef);

  const lessonAvailabilityTimeSlots =
    data.lessonInstructor?.availabilitySlots?.edges?.flatMap((edge) => {
      const slot = edge?.node;

      return slot?.time
        ? [
            {
              id: slot.id,
              time: slot.time,
            },
          ]
        : [];
    }) ?? [];

  return <RequestTimeSelectionForm timeSlots={lessonAvailabilityTimeSlots} />;
};

const RequestTimeSelectionForm = ({
  timeSlots,
}: {
  timeSlots: TimeSlotOption[];
}) => {
  const requestInfo = useAtomValue(getRequestInfoAtom);
  const setRequestInfo = useSetAtom(setRequestInfoAtom);
  const availableTimeSlots = useFilterRequestStartTimeSlots(timeSlots);
  const timeOptions = useGetTimeOptions(availableTimeSlots);
  const requestedTime = requestInfo?.time;
  const minTime = requestInfo?.minTime;
  const maxTime = requestInfo?.maxTime;

  const earlyTimeOptions = useGetEarlyTimeOptions(timeOptions, requestedTime);
  const lateTimeOptions = useGetLateTimeOptions(timeOptions, requestedTime);

  useEffect(() => {
    if (!requestInfo || !requestedTime || timeOptions.length === 0) {
      return;
    }

    const firstTime = timeOptions[0]?.value;
    const lastTime = timeOptions[timeOptions.length - 1]?.value;
    const requestedTimeIsAvailable = timeOptions.some(
      (option) => option.value === requestedTime
    );

    if (requestedTimeIsAvailable && minTime && maxTime) {
      return;
    }

    const nextRequestedTime = requestedTimeIsAvailable
      ? requestedTime
      : timeOptions[0]?.value;
    if (!nextRequestedTime) {
      return;
    }

    setRequestInfo({
      time: nextRequestedTime,
      minTime: minTime || firstTime || nextRequestedTime,
      maxTime: maxTime || lastTime || nextRequestedTime,
    });
  }, [
    maxTime,
    minTime,
    requestInfo,
    requestedTime,
    setRequestInfo,
    timeOptions,
  ]);

  if (!requestInfo) return null;

  const handleRequestedTimeChange = (val: string | null) => {
    if (!val) return;

    // When requested time changes, update min/max times if needed
    const newMinTime = minTime
      ? minTime <= val
        ? minTime
        : val
      : timeOptions[0]?.value || val;
    const newMaxTime = maxTime
      ? maxTime >= val
        ? maxTime
        : val
      : timeOptions[timeOptions.length - 1]?.value || val;

    // Update the request info
    setRequestInfo({
      time: val,
      minTime: newMinTime,
      maxTime: newMaxTime,
    });
  };

  const handleMinTimeChange = (val: string | null) => {
    if (!val) return;

    setRequestInfo({
      ...requestInfo,
      minTime: val,
    });
  };

  const handleMaxTimeChange = (val: string | null) => {
    if (!val) return;

    setRequestInfo({
      ...requestInfo,
      maxTime: val,
    });
  };

  return (
    <VStack pt={2} gap={2}>
      <VStack p={5} pb={2} gap={4} flex={1} bg="surface-primary">
        <Text variant="mainBaseSemiBold">{t`Start Time`}</Text>
        <DropdownSelect
          value={requestedTime}
          placeholder={t`Select a time`}
          options={timeOptions}
          onChange={handleRequestedTimeChange}
          testID="requested-time-select"
        />
      </VStack>

      <HStack gap={4} p={5} pb={2} bg="surface-primary">
        <VStack gap={4} flex={1}>
          <Text variant="mainBaseSemiBold" color="text-primary">
            {t`I can start as early as ...`}
          </Text>
          <DropdownSelect
            value={minTime}
            placeholder={t`Select a time`}
            options={earlyTimeOptions}
            onChange={handleMinTimeChange}
            testID="request-early-time-select"
          />
        </VStack>

        <VStack gap={4} flex={1}>
          <Text variant="mainBaseSemiBold" color="text-primary">
            {t`... and as late as`}
          </Text>
          <DropdownSelect
            value={maxTime}
            placeholder={t`Select a time`}
            options={lateTimeOptions}
            onChange={handleMaxTimeChange}
            testID="request-late-time-select"
          />
        </VStack>
      </HStack>
    </VStack>
  );
};

// Define the fragment that will be used after compilation
const TimeSlotsFragmentString = graphql`
  fragment RequestTimeSelectionControl_TimeSlots on Agenda {
    id
    timeSlots(first: 1000) {
      edges {
        node {
          id
          time
          availability
        }
      }
    }
  }
`;

const LessonAvailabilityQueryString = graphql`
  query RequestTimeSelectionControlLessonAvailabilityQuery(
    $instructorId: ID!
    $dateTimeGte: Datetime!
    $dateTimeLte: Datetime!
    $durationMins: Int!
    $slotAvailability: SlotAvailabilityArgs
  ) {
    lessonInstructor: node(id: $instructorId) {
      ... on StaffProfile {
        availabilitySlots(
          dateTimeGte: $dateTimeGte
          dateTimeLte: $dateTimeLte
          durationMins: $durationMins
          slotAvailability: $slotAvailability
        ) {
          edges {
            node {
              id
              time
            }
          }
        }
      }
    }
  }
`;
